import { useState, useRef, useEffect, useCallback } from 'react';
import { DialogueLine, SoulEntry, EntryTake } from '../types';
import { uploadEntryTake, updateTakeTiming } from '../services/entryService';
import { sanitizeTakes } from '../components/studio/studioUtils';
import { TelemetryEntry } from '../components/studio/TelemetryTerminal';
import { smoothRecordedTake, applyEdgeSmoothingToBuffer } from '../utils/audioSmoothing';

export interface UseStudioRecordingProps {
  ensureHotMic: () => Promise<MediaStream | null>;
  initWebAudioPipeline: () => void;
  dialogueLines: DialogueLine[];
  selectedLineId: string | null;
  selectedLineIds: Set<string>;
  setSelectedLineId: (id: string | null) => void;
  setSelectedLineIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  duration: number;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  vocalAudioRef: React.RefObject<HTMLAudioElement | null>;
  bgmAudioRef: React.RefObject<HTMLAudioElement | null>;
  takeAudioRef: React.RefObject<HTMLAudioElement | null>;
  activeTakeSourceNodeRef: React.MutableRefObject<AudioBufferSourceNode | null>;
  takeBuffersRef: React.MutableRefObject<Map<string, AudioBuffer>>;
  takes: EntryTake[];
  setTakes: React.Dispatch<React.SetStateAction<EntryTake[]>>;
  takesRef: React.MutableRefObject<EntryTake[]>;
  activeEntry: SoulEntry | null;
  setActiveEntry: React.Dispatch<React.SetStateAction<SoulEntry | null>>;
  performerName: string;
  currentTime: number;
  setCurrentTime: (time: number) => void;
  setIsPlaying: (isPlaying: boolean) => void;
  syncPlayback: (play: boolean) => void;
  applyVocalMuting: (time: number, force?: boolean) => void;
  setInspectorFocus: (focus: 'user' | 'orig') => void;
  setHasError: (error: string | null) => void;
  addTelemetryLog: (msg: string, type?: TelemetryEntry['type']) => void;
  getOrCreateActiveEntry: () => Promise<SoulEntry>;
  onEntryUpdated?: (entry: SoulEntry) => void;
  audioCtxRef: React.RefObject<AudioContext | null>;
  isRecordingRef: React.MutableRefObject<boolean>;
  takeGainRef?: React.RefObject<GainNode | null>;
  takeAuditionAudioRef?: React.RefObject<HTMLAudioElement | null>;
  activePlayingTakeIdRef?: React.MutableRefObject<string | null>;
}

export interface UseStudioRecordingReturn {
  isRecording: boolean;
  countdown: number | null;
  isUploadingTake: boolean;
  handleStartRecording: () => Promise<void>;
  handleStopRecording: () => void;
  handleSaveTakesForSelectedLines: (
    audioBlob: Blob,
    targetLines: DialogueLine[],
    lineStart: number,
    lineEnd: number
  ) => Promise<void>;
}

export function useStudioRecording({
  ensureHotMic,
  initWebAudioPipeline,
  dialogueLines,
  selectedLineId,
  selectedLineIds,
  setSelectedLineId,
  setSelectedLineIds,
  duration,
  videoRef,
  vocalAudioRef,
  bgmAudioRef,
  takeAudioRef,
  activeTakeSourceNodeRef,
  takeBuffersRef,
  takes,
  setTakes,
  takesRef,
  activeEntry,
  setActiveEntry,
  performerName,
  currentTime,
  setCurrentTime,
  setIsPlaying,
  syncPlayback,
  applyVocalMuting,
  setInspectorFocus,
  setHasError,
  addTelemetryLog,
  getOrCreateActiveEntry,
  onEntryUpdated,
  audioCtxRef,
  isRecordingRef,
  takeGainRef,
  takeAuditionAudioRef,
  activePlayingTakeIdRef
}: UseStudioRecordingProps): UseStudioRecordingReturn {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isUploadingTake, setIsUploadingTake] = useState<boolean>(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingStopTimeoutRef = useRef<any>(null);
  const recordingArmTimeoutRef = useRef<any>(null);
  const countdownTimerRef = useRef<any>(null);

  // Clean up all timers on unmount
  useEffect(() => {
    return () => {
      isRecordingRef.current = false;
      if (takeGainRef?.current) {
        takeGainRef.current.gain.value = 1.0;
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      if (recordingArmTimeoutRef.current) {
        clearTimeout(recordingArmTimeoutRef.current);
        recordingArmTimeoutRef.current = null;
      }
      if (recordingStopTimeoutRef.current) {
        clearTimeout(recordingStopTimeoutRef.current);
        recordingStopTimeoutRef.current = null;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        try {
          mediaRecorderRef.current.stop();
        } catch (_) {}
      }
    };
  }, [isRecordingRef, takeGainRef]);

  // Save takes to Layer 1 for each selected line
  const handleSaveTakesForSelectedLines = useCallback(
    async (
      rawAudioBlob: Blob,
      targetLines: DialogueLine[],
      lineStart: number,
      lineEnd: number
    ) => {
      setIsUploadingTake(true);
      const tempTakeId = `take-${Date.now()}`;
      const durationSec = Math.max(0.4, lineEnd - lineStart);
      const characterLabel = targetLines[0]?.characterName || targetLines[0]?.speaker || 'Voice';
      const targetLineId = targetLines[0]?.id;

      // Apply edge smoothing: eliminates DC pops & transients at the head and tail
      let audioBlob = rawAudioBlob;
      if (audioCtxRef.current) {
        try {
          const { smoothedBlob, smoothedBuffer } = await smoothRecordedTake(rawAudioBlob, audioCtxRef.current, 50);
          audioBlob = smoothedBlob;
          takeBuffersRef.current.set(tempTakeId, smoothedBuffer);
        } catch (e) {
          console.warn('Edge smoothing notice, applying in-place buffer smoothing:', e);
          rawAudioBlob
            .arrayBuffer()
            .then((ab) => {
              audioCtxRef.current
                ?.decodeAudioData(ab.slice(0))
                .then((decoded) => {
                  applyEdgeSmoothingToBuffer(decoded, 50);
                  takeBuffersRef.current.set(tempTakeId, decoded);
                })
                .catch(() => {});
            })
            .catch(() => {});
        }
      }

      // Instant local playback URL with edge-smoothed audio
      const localAudioUrl = URL.createObjectURL(audioBlob);

      // Focus on target line, switch inspector to user take, and AUTOMATICALLY DISARM the recorded line(s)
      if (targetLines[0]) {
        setSelectedLineId(targetLines[0].id);
        setSelectedLineIds((prev) => {
          const next = new Set(prev);
          targetLines.forEach((l) => {
            if (l.id) next.delete(l.id);
          });
          return next;
        });
        setInspectorFocus('user');
      }

      let resolvedTakeNum = 1;

      // Immediately populate Layer 1 with the new take using block-scoped take numbering
      setTakes((prev) => {
        const blockTakes = prev.filter((t) =>
          t.lineIds && targetLineId ? t.lineIds.includes(targetLineId) : false
        );
        const maxTakeNum = blockTakes.reduce((max, t) => Math.max(max, t.takeNumber || 0), 0);
        resolvedTakeNum = maxTakeNum + 1;

        const updated = prev.map((t) => {
          // Only mark previous take for this exact line as inactive!
          const isPreviousForThisLine = t.lineIds?.includes(targetLineId);
          return isPreviousForThisLine ? { ...t, isActive: false } : t;
        });

        const instantTake: EntryTake = {
          id: tempTakeId,
          entryId: activeEntry?.id || 'session',
          takeNumber: resolvedTakeNum,
          performerName: performerName || 'Artist',
          audioUrl: localAudioUrl,
          duration: durationSec,
          lineIds: targetLines.map((l) => l.id),
          lineStart,
          lineEnd,
          rawStart: lineStart,
          rawEnd: lineEnd,
          characterLabel,
          isActive: true,
          createdAt: Date.now()
        };

        const fullList = sanitizeTakes([...updated, instantTake]);
        takesRef.current = fullList;
        if (activeEntry) {
          const liveEntry: SoulEntry = {
            ...activeEntry,
            takeCount: fullList.length,
            takes: fullList,
            updatedAt: Date.now()
          };
          setActiveEntry(liveEntry);
        }
        return fullList;
      });

      const curTime = videoRef.current ? videoRef.current.currentTime : currentTime;
      applyVocalMuting(curTime, true);

      try {
        const entry = await getOrCreateActiveEntry();

        const savedTake = await uploadEntryTake({
          entryId: entry.id,
          clipId: entry.clipId || activeEntry?.clipId || (targetLines[0] as any)?.clipId || '',
          performerName: performerName || 'Artist',
          audioBlob,
          duration: durationSec,
          lineIds: targetLines.map((l) => l.id),
          lineStart,
          lineEnd,
          characterLabel,
          takeNumber: resolvedTakeNum
        });

        // Also associate decoded buffer with savedTake.id
        const cachedBuf = takeBuffersRef.current.get(tempTakeId);
        if (cachedBuf) {
          takeBuffersRef.current.set(savedTake.id, cachedBuf);
        }

        // Keep local blob URL for instant zero-latency playback while saving backend ID
        setTakes((prev) => {
          const existingTempTake = prev.find((t) => t.id === tempTakeId);
          const currentStart = existingTempTake ? existingTempTake.lineStart : savedTake.lineStart;
          const currentEnd = existingTempTake ? existingTempTake.lineEnd : savedTake.lineEnd;
          const currentRawStart = existingTempTake?.rawStart ?? savedTake.rawStart ?? currentStart;
          const currentRawEnd = existingTempTake?.rawEnd ?? savedTake.rawEnd ?? currentEnd;

          // If user slipped it while upload was in flight, persist to D1 immediately
          if (currentStart !== savedTake.lineStart || currentEnd !== savedTake.lineEnd) {
            updateTakeTiming(savedTake.id, currentStart, currentEnd).catch(() => {});
          }

          const updated = prev.map((t) => {
            if (t.id === tempTakeId) {
              return {
                ...savedTake,
                takeNumber: resolvedTakeNum,
                audioUrl: savedTake.audioUrl || localAudioUrl,
                localAudioUrl: localAudioUrl,
                lineStart: currentStart,
                lineEnd: currentEnd,
                rawStart: currentRawStart,
                rawEnd: currentRawEnd,
                isActive: true
              };
            }
            // Strictly ensure earlier takes for this line are inactive
            if (targetLineId && t.lineIds?.includes(targetLineId)) {
              return { ...t, isActive: false };
            }
            return t;
          });

          const sanitized = sanitizeTakes(updated);
          takesRef.current = sanitized;
          const updatedEntry: SoulEntry = {
            ...entry,
            takeCount: sanitized.length,
            takes: sanitized,
            updatedAt: Date.now()
          };
          setActiveEntry(updatedEntry);
          if (onEntryUpdated) onEntryUpdated(updatedEntry);
          return sanitized;
        });

        addTelemetryLog(`Recorded Take #${resolvedTakeNum} for ${characterLabel}! Line disarmed.`, 'success');
      } catch (err: any) {
        console.warn('Take upload notice (local playback still works):', err);
        addTelemetryLog(`Take saved locally (upload note: ${err.message})`, 'info');
      } finally {
        setIsUploadingTake(false);
        isRecordingRef.current = false;
        if (takeGainRef?.current) {
          takeGainRef.current.gain.cancelScheduledValues(0);
          takeGainRef.current.gain.value = 1.0;
        }
      }
    },
    [
      activeEntry,
      applyVocalMuting,
      audioCtxRef,
      currentTime,
      getOrCreateActiveEntry,
      isRecordingRef,
      onEntryUpdated,
      performerName,
      setActiveEntry,
      setInspectorFocus,
      setSelectedLineId,
      setSelectedLineIds,
      setTakes,
      takeBuffersRef,
      takeGainRef,
      takesRef,
      videoRef,
      addTelemetryLog
    ]
  );

  const handleStopRecording = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(null);
    if (recordingArmTimeoutRef.current) {
      clearTimeout(recordingArmTimeoutRef.current);
      recordingArmTimeoutRef.current = null;
    }
    if (recordingStopTimeoutRef.current) {
      clearTimeout(recordingStopTimeoutRef.current);
      recordingStopTimeoutRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    isRecordingRef.current = false;
    if (activePlayingTakeIdRef) {
      activePlayingTakeIdRef.current = null;
    }
    if (takeGainRef?.current) {
      takeGainRef.current.gain.cancelScheduledValues(0);
      takeGainRef.current.gain.value = 1.0;
    }
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (vocalAudioRef.current) vocalAudioRef.current.pause();
    if (bgmAudioRef.current) bgmAudioRef.current.pause();
    if (activeTakeSourceNodeRef.current) {
      try {
        activeTakeSourceNodeRef.current.stop();
      } catch (_) {}
      activeTakeSourceNodeRef.current = null;
    }
    if (takeAudioRef.current) {
      takeAudioRef.current.pause();
    }
    if (takeAuditionAudioRef?.current) {
      takeAuditionAudioRef.current.pause();
    }
    setIsPlaying(false);
    syncPlayback(false);
    addTelemetryLog('Recording and playback stopped by user command.', 'info');
  }, [
    activePlayingTakeIdRef,
    activeTakeSourceNodeRef,
    bgmAudioRef,
    isRecordingRef,
    setIsPlaying,
    syncPlayback,
    takeAudioRef,
    takeAuditionAudioRef,
    takeGainRef,
    videoRef,
    vocalAudioRef,
    addTelemetryLog
  ]);

  const handleStartRecording = useCallback(async () => {
    initWebAudioPipeline();
    setHasError(null);

    // Strictly silence and stop any existing take audition, playback, or buffer node IMMEDIATELY
    isRecordingRef.current = true;
    if (activePlayingTakeIdRef) {
      activePlayingTakeIdRef.current = null;
    }
    if (activeTakeSourceNodeRef.current) {
      try { activeTakeSourceNodeRef.current.stop(); } catch (_) {}
      activeTakeSourceNodeRef.current = null;
    }
    if (takeAudioRef.current) {
      takeAudioRef.current.pause();
      takeAudioRef.current.currentTime = 0;
    }
    if (takeAuditionAudioRef?.current) {
      takeAuditionAudioRef.current.pause();
      takeAuditionAudioRef.current.currentTime = 0;
    }
    if (takeGainRef?.current) {
      takeGainRef.current.gain.cancelScheduledValues(0);
      takeGainRef.current.gain.value = 0.0;
    }

    // 1. Ensure Hot Mic is active (zero OS arming latency)
    const stream = await ensureHotMic();
    if (!stream) {
      isRecordingRef.current = false;
      if (takeGainRef?.current) {
        takeGainRef.current.gain.value = 1.0;
      }
      setHasError('Microphone not available. Please allow microphone permissions in your browser.');
      return;
    }
    recordingStreamRef.current = stream;

    // 2. Identify target line to record (must be armed/selected):
    if (selectedLineIds.size === 0) {
      isRecordingRef.current = false;
      if (takeGainRef?.current) {
        takeGainRef.current.gain.value = 1.0;
      }
      setHasError('Please select at least one dialogue line in the teleprompter to record.');
      return;
    }

    let targetLine: DialogueLine | null = null;
    if (selectedLineId && selectedLineIds.has(selectedLineId)) {
      targetLine = dialogueLines.find((l) => l.id === selectedLineId) || null;
    }
    if (!targetLine && selectedLineIds.size > 0) {
      const firstId = Array.from(selectedLineIds)[0];
      targetLine = dialogueLines.find((l) => l.id === firstId) || null;
    }

    if (!targetLine) {
      isRecordingRef.current = false;
      if (takeGainRef?.current) {
        takeGainRef.current.gain.value = 1.0;
      }
      setHasError('No valid selected dialogue line available to record.');
      return;
    }

    const linesToRecord = [targetLine];
    setSelectedLineId(targetLine.id);

    const minStart = Math.max(0, targetLine.start ?? targetLine.startTime ?? 0);
    const maxEnd = Math.min(duration, targetLine.end ?? targetLine.endTime ?? duration);

    // Option A: 250ms head padding for zero-pop startup & pre-speech respiration, 150ms tail padding
    const CAPTURE_START_PADDING = 0.25;
    const CAPTURE_END_PADDING = 0.15;
    const recordStart = Math.max(0, minStart - CAPTURE_START_PADDING);
    const recordEnd = Math.min(duration, maxEnd + CAPTURE_END_PADDING);

    try {
      let mimeType = 'audio/webm;codecs=opus';
      if (typeof MediaRecorder !== 'undefined') {
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        }
      }

      const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 256000 });
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        // NOTE: Keep mic HOT! Do NOT stop stream tracks so future takes arm with 0ms delay.
        await handleSaveTakesForSelectedLines(audioBlob, linesToRecord, recordStart, recordEnd);
      };

      // 3. ADR Pre-roll lead-in cue (2.0s before recordStart)
      const preRollStart = Math.max(0, recordStart - 2.0);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = preRollStart;
        setCurrentTime(preRollStart);
      }
      if (vocalAudioRef.current) vocalAudioRef.current.currentTime = preRollStart;
      if (bgmAudioRef.current) bgmAudioRef.current.currentTime = preRollStart;

      // 4. Visual 3..2..1 Countdown
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      setCountdown(3);
      let count = 3;
      const countTimer = setInterval(() => {
        count -= 1;
        if (count <= 0) {
          clearInterval(countTimer);
          countdownTimerRef.current = null;
          setCountdown(null);
          isRecordingRef.current = true;
          setIsRecording(true);

          if (takeGainRef?.current) {
            takeGainRef.current.gain.cancelScheduledValues(0);
            takeGainRef.current.gain.value = 0.0;
          }
          if (activeTakeSourceNodeRef.current) {
            try { activeTakeSourceNodeRef.current.stop(); } catch (_) {}
            activeTakeSourceNodeRef.current = null;
          }
          if (takeAudioRef.current && !takeAudioRef.current.paused) {
            takeAudioRef.current.pause();
          }

          if (videoRef.current) {
            videoRef.current.currentTime = preRollStart;
            videoRef.current.play().catch(() => {});
            setIsPlaying(true);
            syncPlayback(true);
          }

          const msUntilRecordStart = Math.max(0, Math.round((recordStart - preRollStart) * 1000));
          const msDurationToRecord = Math.max(600, Math.round((recordEnd - recordStart) * 1000));

          if (msUntilRecordStart === 0) {
            // Target line is right at the start
            recorder.start(100);
          } else {
            // Video plays pre-roll cue; arm recording precisely 250ms before dialogue begins
            recordingArmTimeoutRef.current = setTimeout(() => {
              if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'inactive') {
                mediaRecorderRef.current.start(100);
              }
            }, msUntilRecordStart);
          }

          // Automatically finish audio capture when dialogue window concludes + tail padding
          recordingStopTimeoutRef.current = setTimeout(() => {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              mediaRecorderRef.current.stop();
            }
            setIsRecording(false);
            addTelemetryLog(`Dialogue take recorded! Playback continuing through scene...`, 'info');
          }, msUntilRecordStart + msDurationToRecord);
        } else {
          setCountdown(count);
        }
      }, 1000);
      countdownTimerRef.current = countTimer;
    } catch (err: any) {
      isRecordingRef.current = false;
      if (takeGainRef?.current) {
        takeGainRef.current.gain.value = 1.0;
      }
      setHasError(`Microphone error: ${err.message}`);
    }
  }, [
    activePlayingTakeIdRef,
    activeTakeSourceNodeRef,
    bgmAudioRef,
    dialogueLines,
    duration,
    ensureHotMic,
    handleSaveTakesForSelectedLines,
    initWebAudioPipeline,
    isRecordingRef,
    selectedLineId,
    selectedLineIds,
    setCurrentTime,
    setHasError,
    setIsPlaying,
    setSelectedLineId,
    setSelectedLineIds,
    syncPlayback,
    takeAudioRef,
    takeAuditionAudioRef,
    takeGainRef,
    videoRef,
    vocalAudioRef,
    addTelemetryLog
  ]);

  return {
    isRecording,
    countdown,
    isUploadingTake,
    handleStartRecording,
    handleStopRecording,
    handleSaveTakesForSelectedLines
  };
}
